import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Droplets, Bug, Sprout, Check, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Evening wrap-up modal — quick 30-second daily check-in.
 *  Trigger via setOpen(true) from any page.
 */
export function EveningCheckIn({ open, onClose }) {
  const [step, setStep] = useState(0); // 0=questions, 1=celebration
  const [state, setState] = useState({ watered: false, spotted_issues: false, harvested: false, general_notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyToday, setAlreadyToday] = useState(false);

  useEffect(() => {
    if (open) {
      // Check if already checked-in today
      axios.get(`${API}/check-in/today`, { withCredentials: true }).then(res => {
        setAlreadyToday(res.data.checked_today);
      }).catch(() => {});
    } else {
      setStep(0); setResult(null);
      setState({ watered: false, spotted_issues: false, harvested: false, general_notes: '' });
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/check-in/evening`, state, { withCredentials: true });
      setResult(res.data);
      setStep(1);
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const markLessonSeen = async () => {
    if (result?.micro_tip?.id) {
      try { await axios.post(`${API}/micro-lessons/${result.micro_tip.id}/seen`, {}, { withCredentials: true }); } catch (e) { /* noop */ }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="evening-check-in-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#7A6B57]"><X className="w-5 h-5" /></button>
        {step === 0 && (
          <>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#C9A85B]">Evening wrap-up</p>
            <h3 className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>30-second check-in</h3>
            <p className="text-sm text-[#7A6B57] mt-2">Quick — how was your garden today?</p>
            {alreadyToday && <p className="text-xs text-[#6B9F4A] mt-2 italic">You&apos;ve already checked in today — updating your entry.</p>}

            <div className="space-y-3 mt-5">
              <QuestionToggle icon={Droplets} label="Did you water?" testId="checkin-watered"
                active={state.watered} onClick={() => setState({ ...state, watered: !state.watered })} />
              <QuestionToggle icon={Bug} label="Spot any pests or issues?" testId="checkin-issues"
                active={state.spotted_issues} onClick={() => setState({ ...state, spotted_issues: !state.spotted_issues })} />
              <QuestionToggle icon={Sprout} label="Harvest anything?" testId="checkin-harvested"
                active={state.harvested} onClick={() => setState({ ...state, harvested: !state.harvested })} />
              <textarea placeholder="Anything to remember? (optional)" value={state.general_notes}
                onChange={e => setState({ ...state, general_notes: e.target.value })}
                rows={2} data-testid="checkin-notes"
                className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 text-sm outline-none focus:border-[#1A4331]" />
            </div>

            <button onClick={submit} disabled={submitting} data-testid="checkin-submit"
              className="w-full mt-5 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">
              {submitting ? 'Saving...' : 'Log check-in'}
            </button>
          </>
        )}

        {step === 1 && result && (
          <div data-testid="checkin-result">
            <div className="text-center">
              <Sparkles className="w-10 h-10 text-[#C9A85B] mx-auto mb-2" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#C9A85B]">Beautiful</p>
              <h3 className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{result.streak} day{result.streak === 1 ? '' : 's'} in a row</h3>
              <p className="text-sm text-[#7A6B57] mt-2">{result.encouragement}</p>
              <p className="text-xs text-[#C9A85B] mt-2 font-semibold">+{result.xp_awarded} XP earned</p>
            </div>

            {result.micro_tip && (
              <div className="mt-5 bg-gradient-to-br from-[#C9A85B]/10 to-[#FAF6EC] rounded-2xl p-4 border border-[#C9A85B]/30" data-testid="checkin-micro-tip">
                <p className="text-xs font-bold tracking-wider uppercase text-[#C9A85B]">Quick lesson · {result.micro_tip.category}</p>
                <p className="font-semibold text-[#1A4331] mt-1">{result.micro_tip.title}</p>
                <p className="text-sm text-[#2A3528] mt-2 leading-relaxed">{result.micro_tip.body}</p>
              </div>
            )}

            <button onClick={() => { markLessonSeen(); onClose(); }} data-testid="checkin-done"
              className="w-full mt-5 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionToggle({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all ${active ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-[#E5EBDD] bg-white hover:border-[#8EAA97]'}`}>
      <Icon className={`w-5 h-5 ${active ? 'text-[#1A4331]' : 'text-[#7A6B57]'}`} />
      <span className={`flex-1 text-left font-medium ${active ? 'text-[#1A4331]' : 'text-[#2A3528]'}`}>{label}</span>
      {active && <Check className="w-4 h-4 text-[#1A4331]" />}
    </button>
  );
}

/** Mark Failed modal — empathetic flow for planting failure. */
export function MarkFailedModal({ open, planting, onClose, onMarked }) {
  const [reasons, setReasons] = useState([]);
  const [picked, setPicked] = useState(null);
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState(0); // 0=reason, 1=advice
  const [submitting, setSubmitting] = useState(false);
  const [advice, setAdvice] = useState(null);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/failure-reasons`, { withCredentials: true }).then(r => setReasons(r.data)).catch(() => {});
    } else {
      setPicked(null); setStep(0); setAdvice(null); setNotes('');
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/plantings/${planting.planting_id}/mark-failed`, { reason: picked, notes }, { withCredentials: true });
      setAdvice(res.data);
      setStep(1);
      onMarked && onMarked();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  if (!open || !planting) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="mark-failed-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#7A6B57]"><X className="w-5 h-5" /></button>

        {step === 0 && (
          <>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#C66B4A]">It happens</p>
            <h3 className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>What went wrong with your {planting.crop_name}?</h3>
            <p className="text-sm text-[#7A6B57] mt-2">Every failure teaches more than a success. Pick the closest reason — we&apos;ll send personalised advice for next time.</p>

            <div className="space-y-2 mt-5">
              {reasons.map(r => (
                <button key={r.id} onClick={() => setPicked(r.id)} data-testid={`failed-reason-${r.id}`}
                  className={`w-full text-left p-3 rounded-2xl border-2 transition-all ${picked === r.id ? 'border-[#C66B4A] bg-[#C66B4A]/5' : 'border-[#E5EBDD] bg-white hover:border-[#8EAA97]'}`}>
                  <p className="font-semibold text-[#1A4331] text-sm">{r.label}</p>
                </button>
              ))}
            </div>

            <textarea placeholder="Any extra context? (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 text-sm outline-none focus:border-[#1A4331] mt-4" />

            <button onClick={submit} disabled={!picked || submitting} data-testid="mark-failed-submit"
              className="w-full mt-4 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50">
              {submitting ? 'Saving...' : 'Log it & get advice'}
            </button>
          </>
        )}

        {step === 1 && advice && (
          <div data-testid="mark-failed-advice">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B9F4A]">Mistake → Mastery</p>
            <h3 className="text-2xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{advice.label}</h3>
            <p className="text-sm text-[#2A3528] italic mt-3 leading-relaxed">{advice.tone}</p>

            <div className="mt-5 bg-white/50 rounded-2xl p-4">
              <p className="text-xs font-bold tracking-wider uppercase text-[#1A4331] mb-2">What we suggest</p>
              <p className="text-sm text-[#2A3528] leading-relaxed">{advice.advice}</p>
            </div>

            <div className="mt-3 bg-[#6B9F4A]/10 rounded-2xl p-4">
              <p className="text-xs font-bold tracking-wider uppercase text-[#6B9F4A] mb-2">Next time</p>
              <ul className="space-y-1">
                {advice.next_steps.map((s, i) => <li key={i} className="text-sm text-[#2A3528]">• {s}</li>)}
              </ul>
            </div>

            <p className="text-xs text-[#C9A85B] mt-4 italic">{advice.encouragement}</p>

            <button onClick={onClose} data-testid="mark-failed-close"
              className="w-full mt-4 bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] py-3 rounded-full font-semibold">
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Micro-lesson toast — small unobtrusive learning popup */
export function MicroLessonToast({ context, onClose }) {
  const [lesson, setLesson] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/micro-lessons/random${context ? `?context=${context}` : ''}`, { withCredentials: true })
      .then(r => setLesson(r.data)).catch(() => {});
  }, [context]);

  const markSeen = async () => {
    if (lesson?.id) {
      try { await axios.post(`${API}/micro-lessons/${lesson.id}/seen`, {}, { withCredentials: true }); } catch (e) { /* noop */ }
    }
    setDismissed(true);
    onClose && onClose();
  };

  if (!lesson || dismissed) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 max-w-sm" data-testid="micro-lesson-toast">
      <div className="bg-gradient-to-br from-[#C9A85B]/10 to-[#FAF6EC] rounded-2xl p-4 border border-[#C9A85B]/30 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-[#C9A85B]/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#C9A85B]" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold tracking-wider uppercase text-[#C9A85B]">Quick lesson · +10 XP</p>
            <p className="font-semibold text-[#1A4331] mt-1 text-sm">{lesson.title}</p>
            <p className="text-xs text-[#2A3528] mt-1 leading-relaxed">{lesson.body}</p>
            <button onClick={markSeen} data-testid="micro-lesson-dismiss" className="text-xs text-[#C9A85B] font-semibold mt-2">Got it →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
